import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { get } from 'lodash';

import { ApiCalls, type ApiLoadingState, ErrorCauses } from '../../model/api.types';
import type { State } from '../../model/general.types';

const initialState: ApiLoadingState = {
    loading: {
        [ApiCalls.ASSETS]: false,
        [ApiCalls.TRACKING_PUT_CREATE]: false,
        [ApiCalls.TRACKING_PUT_UPDATE]: false,
        [ApiCalls.TRACKINGS_FETCH]: false,
    },
    error: {
        [ErrorCauses.ASSETS_FETCH]: false,
        [ErrorCauses.TRACKING_DELETE]: false,
        [ErrorCauses.TRACKING_PUT_CREATE]: false,
        [ErrorCauses.TRACKING_PUT_UPDATE]: false,
        [ErrorCauses.TRACKINGS_FETCH]: false,
    },
};

const name = 'api';

const { reducer, actions } = createSlice({
    name,
    initialState,
    reducers: {
        setLoadingForApiCall: (
            state: ApiLoadingState,
            action: PayloadAction<{ type: ApiCalls; value: boolean }>
        ): void => {
            state.loading = {
                ...state.loading,
                [action.payload.type]: action.payload.value,
            };
        },
        setErrorForApiCall: (
            state: ApiLoadingState,
            action: PayloadAction<{ type: ErrorCauses; value: boolean }>
        ): void => {
            state.error = {
                ...state.error,
                [action.payload.type]: action.payload.value,
            };
        },
    },
});

export const apiCallActions = actions;
export const apiCallReducer = reducer;

const getBase = (state: State): ApiLoadingState | undefined => get(state, 'api');
const getLoading = (state: State): { [key: string]: boolean } | undefined => get(getBase(state), 'loading');
const getError = (state: State): { [key: string]: boolean } | undefined => get(getBase(state), 'error');

export const getIsAssetsLoading = (state: State): boolean => get(getLoading(state), ApiCalls.ASSETS, false);
export const getHasAssetsFetchError = (state: State): boolean => get(getError(state), ErrorCauses.ASSETS_FETCH, false);

export const getIsTrackingsFetchLoading = (state: State): boolean =>
    get(getLoading(state), ApiCalls.TRACKINGS_FETCH, false);
export const getHasTrackingsFetchError = (state: State): boolean =>
    get(getError(state), ErrorCauses.TRACKINGS_FETCH, false);

export const getHasTrackingDeleteError = (state: State): boolean =>
    get(getError(state), ErrorCauses.TRACKING_DELETE, false);

export const getIsTrackingPutCreateOngoing = (state: State): boolean =>
    get(getLoading(state), ApiCalls.TRACKING_PUT_CREATE, false);
export const getHasTrackingPutCreateError = (state: State): boolean =>
    get(getError(state), ErrorCauses.TRACKING_PUT_CREATE, false);

export const getIsTrackingPutUpdateOngoing = (state: State): boolean =>
    get(getLoading(state), ApiCalls.TRACKING_PUT_UPDATE, false);
export const getHasTrackingPutUpdateError = (state: State): boolean =>
    get(getError(state), ErrorCauses.TRACKING_PUT_UPDATE, false);
